import React, { useState } from 'react';
import axios from 'axios';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Appel à l'API Azure Functions pour SendGrid
      const response = await axios.post('/api/send-email', {
        to: 'contact@askeko.fr', // Email de destination
        subject: `Nouveau message de ${formData.name}: ${formData.subject}`,
        text: `
          Nom: ${formData.name}
          Email: ${formData.email}
          Sujet: ${formData.subject}
          
          Message:
          ${formData.message}
        `,
        html: `
          <h3>Nouveau message de contact</h3>
          <p><strong>Nom:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Sujet:</strong> ${formData.subject}</p>
          <hr>
          <p><strong>Message:</strong></p>
          <p>${formData.message.replace(/\n/g, '<br>')}</p>
        `
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <h2 className="section-title">Contactez-nous</h2>
        <p className="section-subtitle">Vous souhaitez en savoir plus sur AskEko ? N'hésitez pas à nous contacter.</p>
        
        {submitStatus === 'success' && (
          <div className="success-message">
            ✅ Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="error-message" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            ❌ Une erreur est survenue lors de l'envoi du message. Veuillez réessayer.
          </div>
        )}

        <div className="contact-grid">
          <div className="contact-info">
            <div className="info-section">
              <h3>Informations</h3>
              <div className="info-content">
                <p><strong>Siège social :</strong></p>
                <p>Immeuble PARK PLAZA 2 – HUB INNOVATION</p>
                <p>Parc Scientifique de la Haute Borne</p>
                <p>11-B avenue de l'Harmonie</p>
                <p>59650 VILLENEUVE D'ASCQ</p>
                <p>contact@askeko.fr</p>
              </div>
            </div>

            <div className="info-section">
              <h3>Fondatrices</h3>
              <div className="founders">
                <div className="founder">
                  <h4>Agnès TRINQUIER</h4>
                  <p>Direction commerciale, développement new business B2B2C</p>
                </div>
                <div className="founder">
                  <h4>Valérie DELPORTE</h4>
                  <p>Marketing, stratégie omnicanale, partenariats</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-section">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nom *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Votre nom"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="votre.email@entreprise.com"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="subject">Sujet *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={errors.subject ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Demande d'information AskEko"
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={errors.message ? 'error' : ''}
                  disabled={isSubmitting}
                  placeholder="Bonjour, je souhaite en savoir plus sur AskEko..."
                />
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="cta-button"
                disabled={isSubmitting}
                style={{ width: '100%' }}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
